import { getAddressDataFromPlaceDetails } from '@choco-admin/utils';
import { ChangeEvent, useCallback, useRef, useState, useEffect } from 'react';

import type { AddressChangeHandler, AddressData } from '../types';

import type { PlaceDetails } from './useGooglePlaces';
import { useGooglePlaces } from './useGooglePlaces';

// Local interface for Google Places predictions
interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

interface FormattedPrediction {
  placeId: string;
  description: string;
}

interface UseAddressInputProps {
  onAddressChange: AddressChangeHandler<AddressData>;
  isCustom?: boolean;
  onManualInput?: (value: string) => void;
}

const formatPrediction = ({
  place_id: placeId,
  description,
}: PlacePrediction): FormattedPrediction => ({
  placeId,
  description,
});

const predictionsConfig = {
  placeTypes: ['address'],
};

export const useAddressInput = ({
  isCustom = false,
  onAddressChange,
  onManualInput,
}: UseAddressInputProps) => {
  const [predictionsVisible, setPredictionsVisible] = useState(false);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const predictionsListRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close predictions
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      inputContainerRef.current &&
      !inputContainerRef.current.contains(event.target as Node)
    ) {
      setPredictionsVisible(false);
    }
  }, []);

  const showPredictions = useCallback(() => {
    setPredictionsVisible(true);
    document.addEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const hidePredictions = useCallback(() => {
    setPredictionsVisible(false);
    document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  // Place details handlers
  const onPlaceDetailsRequestSuccess = useCallback(
    (placeDetails: PlaceDetails) => {
      const addressData = getAddressDataFromPlaceDetails(
        placeDetails,
        isCustom
      );
      onAddressChange(addressData);
    },
    [onAddressChange, isCustom]
  );

  const onPlaceDetailsRequestError = useCallback((error: Error) => {
    // Log error silently - in production this would go to error reporting
    // eslint-disable-next-line no-console
    console.error('Error fetching place details:', error);
  }, []);

  // Use the Google Places hook
  const { predictions, ...placesInfo } = useGooglePlaces({
    onPlaceDetailsRequestSuccess,
    onPlaceDetailsRequestError,
  });

  const formattedPredictions = predictions.map(formatPrediction);

  const handleInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      // Call manual input handler if provided
      if (onManualInput) {
        onManualInput(value);
      }

      placesInfo.fetchPlacePredictions({
        searchTerm: value,
        ...(isCustom && predictionsConfig),
      });

      if (value.trim()) {
        showPredictions();
      } else {
        hidePredictions();
      }
    },
    [isCustom, placesInfo, showPredictions, hidePredictions, onManualInput]
  );

  const onGooglePlaceSelect = useCallback(
    ({ placeId }: FormattedPrediction) => {
      placesInfo.fetchPlaceDetails({ placeId });
      hidePredictions();
    },
    [placesInfo, hidePredictions]
  );

  const onCreateCustomBuyerClick = useCallback(() => {
    onAddressChange({
      addressSource: 'custom',
      fullAddress: '',
      name: '',
    });
    hidePredictions();
  }, [onAddressChange, hidePredictions]);

  // Clean up event listener on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return {
    ...placesInfo,
    predictionsAreVisible: predictionsVisible,
    predictions: formattedPredictions,
    handleInputChange,
    onGooglePlaceSelect,
    onCreateCustomBuyerClick,
    inputContainerRef,
    predictionsListRef,
    showPredictions,
    hidePredictions,
  };
};
